import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  InputNumber,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { BulbOutlined } from '@ant-design/icons';
import {
  approveProposedTrade,
  getPortfolioProposedTrades,
  getPortfolioRebalanceAnalysis,
  getPortfolioRebalanceInsights,
  getRebalanceInsightsStatus,
  proposePortfolioRebalance,
  rejectProposedTrade,
} from '../services/api';
import { alexforbesBrand } from '../brand/alexforbes';
import EditableMarkdownContent from './EditableMarkdownContent';

const { Text, Title } = Typography;

const PortfolioRebalancePanel = ({ portfolioId }) => {
  const [loading, setLoading] = useState(true);
  const [proposing, setProposing] = useState(false);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [aiConfigured, setAiConfigured] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insightsMarkdown, setInsightsMarkdown] = useState('');
  const [insightsError, setInsightsError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [cashContribution, setCashContribution] = useState(null);
  const [cashWithdrawal, setCashWithdrawal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, p, aiStatus] = await Promise.all([
        getPortfolioRebalanceAnalysis(portfolioId),
        getPortfolioProposedTrades(portfolioId, { status: 'pending_approval' }),
        getRebalanceInsightsStatus(portfolioId).catch(() => ({ configured: false })),
      ]);
      setAnalysis(a);
      setProposals(Array.isArray(p) ? p : []);
      setAiConfigured(Boolean(aiStatus?.configured));
    } catch (e) {
      message.error('Failed to load rebalance data');
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePropose = async () => {
    setProposing(true);
    setInsights(null);
    setInsightsMarkdown('');
    setInsightsError(null);
    try {
      const payload = {};
      if (cashContribution != null) payload.cash_contribution = cashContribution;
      if (cashWithdrawal != null) payload.cash_withdrawal = cashWithdrawal;
      const result = await proposePortfolioRebalance(portfolioId, payload);
      if (result.error) {
        message.warning(result.error);
      } else {
        message.success(`Generated ${result.proposed_count || 0} proposed trade(s)`);
      }
      await load();
    } catch (e) {
      message.error('Failed to generate rebalance proposals');
    } finally {
      setProposing(false);
    }
  };

  const handleGetInsights = async () => {
    setInsightsLoading(true);
    setInsightsError(null);
    try {
      const result = await getPortfolioRebalanceInsights(portfolioId);
      if (result.error && !result.insights) {
        setInsightsError(result.error);
        setInsights(null);
      } else {
        const text = result.insights || '';
        setInsights(text);
        setInsightsMarkdown(text);
        message.success('Investment analyst insights ready');
      }
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        'Failed to generate insights. Check OPENAI_API_KEY on the server.';
      setInsightsError(msg);
      setInsights(null);
    } finally {
      setInsightsLoading(false);
    }
  };

  const handleApprove = async (proposalId) => {
    try {
      const res = await approveProposedTrade(portfolioId, proposalId);
      message.success(`Approved — draft trade ${res.trade_id}`);
      await load();
    } catch (e) {
      message.error('Failed to approve proposal');
    }
  };

  const handleReject = async (proposalId) => {
    try {
      await rejectProposedTrade(portfolioId, proposalId);
      message.success('Proposal rejected');
      await load();
    } catch (e) {
      message.error('Failed to reject proposal');
    }
  };

  if (loading) {
    return <Spin />;
  }

  if (analysis?.error) {
    return <Alert type="warning" showIcon message={analysis.error} />;
  }

  const lineColumns = [
    {
      title: 'Instrument',
      key: 'name',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text>{row.instrument_name}</Text>
          <Text type="secondary">{row.isin}</Text>
        </Space>
      ),
    },
    { title: 'Target %', dataIndex: 'target_weight', width: 90 },
    { title: 'Min %', dataIndex: 'min_weight', width: 80 },
    { title: 'Max %', dataIndex: 'max_weight', width: 80 },
    { title: 'Actual %', dataIndex: 'actual_weight', width: 90 },
    {
      title: 'Band',
      key: 'band',
      width: 100,
      render: (_, row) =>
        row.outside_band ? (
          <Tag color={row.action === 'buy' ? alexforbesBrand.colors.orange : 'volcano'}>
            {row.action?.toUpperCase()}
          </Tag>
        ) : (
          <Tag>OK</Tag>
        ),
    },
    {
      title: 'Gap (base ccy)',
      dataIndex: 'difference',
      render: (v) => {
        const n = Number(v);
        const color = n > 0 ? alexforbesBrand.colors.teal : n < 0 ? '#c62828' : undefined;
        return <Text style={{ color }}>{v}</Text>;
      },
    },
  ];

  const proposalColumns = [
    {
      title: 'Instrument',
      key: 'inst',
      render: (_, row) => row.instrument?.name || row.instrument_name,
    },
    {
      title: 'Side',
      dataIndex: 'operation',
      render: (op) => (
        <Tag color={op === 'buy' ? alexforbesBrand.colors.orange : 'red'}>{op?.toUpperCase()}</Tag>
      ),
    },
    { title: 'Amount', dataIndex: 'trade_amount' },
    { title: 'Qty', dataIndex: 'quantity' },
    {
      title: 'Liquidity',
      key: 'liquidity',
      width: 160,
      render: (_, row) => {
        const inst = row.instrument || {};
        const notice = inst.notice_period_days;
        const freq = inst.dealing_frequency;
        if (!notice && (!freq || freq === 'daily')) return '—';
        const parts = [];
        if (freq) parts.push(String(freq).replace(/_/g, ' '));
        if (notice) parts.push(`${notice}d notice`);
        if (inst.gate_percent != null && inst.gate_percent !== '') {
          parts.push(`${inst.gate_percent}% gate`);
        }
        return parts.join(' · ') || '—';
      },
    },
    { title: 'Reason', dataIndex: 'reason', ellipsis: true },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, row) => (
        <Space>
          <Button size="small" type="primary" onClick={() => handleApprove(row.id)}>
            Approve
          </Button>
          <Button size="small" danger onClick={() => handleReject(row.id)}>
            Reject
          </Button>
        </Space>
      ),
    },
  ];

  const canRequestInsights = aiConfigured;
  const insightsDisabledReason = !aiConfigured
    ? 'Set OPENAI_API_KEY on the server to enable AI insights'
    : null;

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Text type="secondary">Total value</Text>
            <Title level={5} style={{ margin: 0 }}>
              {analysis.total_value} {analysis.reporting_currency}
            </Title>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Text type="secondary">Cash weight</Text>
            <Title level={5} style={{ margin: 0 }}>
              {analysis.cash_weight}% (min {analysis.minimum_cash_percentage}%)
            </Title>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Text type="secondary">Outside tolerance</Text>
            <Title level={5} style={{ margin: 0 }}>
              {analysis.lines_outside_band} line(s)
            </Title>
          </Card>
        </Col>
      </Row>

      {analysis.cash_below_minimum && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message="Cash is below the minimum buffer"
        />
      )}

      <Card size="small" title="Generate rebalance proposals" style={{ marginBottom: 16 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Trades are suggested only when holdings fall outside min/max bands. New cash allocates to
          underweight lines; withdrawals sell overweight positions first.
        </Text>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={6}>
            <InputNumber
              min={0}
              placeholder="Cash contribution"
              style={{ width: '100%' }}
              value={cashContribution}
              onChange={setCashContribution}
            />
          </Col>
          <Col xs={24} md={6}>
            <InputNumber
              min={0}
              placeholder="Cash withdrawal"
              style={{ width: '100%' }}
              value={cashWithdrawal}
              onChange={setCashWithdrawal}
            />
          </Col>
          <Col xs={24} md={6}>
            <Button type="primary" loading={proposing} onClick={handlePropose} block>
              Propose rebalance
            </Button>
          </Col>
          <Col xs={24} md={6}>
            <Button
              icon={<BulbOutlined />}
              loading={insightsLoading}
              onClick={handleGetInsights}
              disabled={!canRequestInsights}
              title={insightsDisabledReason || undefined}
              block
              style={
                canRequestInsights
                  ? { borderColor: alexforbesBrand.colors.orange, color: alexforbesBrand.colors.orange }
                  : undefined
              }
            >
              Get AI insights
            </Button>
          </Col>
        </Row>
        {!aiConfigured && (
          <Alert
            style={{ marginTop: 12 }}
            type="info"
            showIcon
            message="AI insights are optional"
            description="Configure OPENAI_API_KEY in the server environment to enable investment analyst commentary on proposed trades."
          />
        )}
      </Card>

      {(insights || insightsError) && (
        <Card
          size="small"
          title="Investment analyst insights"
          style={{ marginBottom: 16 }}
          extra={
            insights ? (
              <Space size="small">
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    setInsights(null);
                    setInsightsMarkdown('');
                  }}
                >
                  Dismiss
                </Button>
              </Space>
            ) : null
          }
        >
          {insightsError && (
            <Alert type="error" showIcon message={insightsError} style={{ marginBottom: 12 }} />
          )}
          {insights && (
            <EditableMarkdownContent
              value={insightsMarkdown}
              onChange={setInsightsMarkdown}
              minRows={16}
              defaultTab="preview"
            />
          )}
        </Card>
      )}

      <Card size="small" title="Allocation vs model" style={{ marginBottom: 16 }}>
        <Table
          rowKey="instrument_id"
          dataSource={analysis.lines || []}
          columns={lineColumns}
          pagination={false}
          size="small"
        />
      </Card>

      <Card size="small" title="Pending proposed trades">
        <Table
          rowKey="id"
          dataSource={proposals}
          columns={proposalColumns}
          pagination={false}
          locale={{ emptyText: 'No pending proposals — run Propose rebalance, then Get AI insights' }}
          size="small"
        />
      </Card>
    </>
  );
};

export default PortfolioRebalancePanel;
